import { useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { CONDITIONS, getCondition, getRelated } from "@/data/conditions";

const WA_BOOK = "https://wa.me/919871053777?text=Hi%20PhysioSewa%2C%20I%20would%20like%20to%20book%20an%20appointment";
const WA_ENQUIRY = "https://wa.me/919871053777?text=Hi%20PhysioSewa%2C%20I%20have%20an%20enquiry";

// Unique, search-focused titles + descriptions per specialty
const SLUG_META = {
  "pain-management":                      { title: "Back Pain & Knee Pain Physiotherapy in Gurugram | PhysioSewa",             desc: "Physiotherapy for back, neck, knee, shoulder, sciatica and postural pain in Sector 110, Gurugram. Clinic + home visits. Call 9871053777." },
  "neurological-rehabilitation":          { title: "Neurological Rehabilitation in Gurugram | Stroke, Parkinson's | PhysioSewa", desc: "Stroke, Parkinson's, facial palsy and balance rehabilitation in Sector 110, Gurugram. Same physiotherapist every session. Call 9871053777." },
  "geriatric-rehabilitation":             { title: "Geriatric Physiotherapy in Gurugram | Fall Prevention | PhysioSewa",         desc: "Gentle physiotherapy for older adults in Sector 110, Gurugram — joint pain, mobility, fall prevention. Home visits available. Call 9871053777." },
  "orthopaedic-sports-rehabilitation":    { title: "Sports Injury & Post-Surgery Rehab in Gurugram | PhysioSewa",                 desc: "Fracture, ACL, knee/shoulder/spine post-surgery and sports injury rehabilitation in Sector 110, Gurugram. Call 9871053777." },
  "maternity-care":                       { title: "Prenatal & Postnatal Physiotherapy in Gurugram | PhysioSewa",                 desc: "Pregnancy, LSCS recovery, pelvic floor and core physiotherapy in Sector 110, Gurugram. Female physiotherapist. Call 9871053777." },
  "manual-therapy":                       { title: "Dry Needling & Manual Therapy in Gurugram | PhysioSewa",                      desc: "Joint mobilisation, myofascial release, dry needling and cupping therapy in Sector 110, Gurugram. Call 9871053777." },
};

// Old slugs → new canonical slugs (so legacy /services/... redirects to a stable URL)
const SLUG_ALIAS = {
  "neuro-rehab":     "neurological-rehabilitation",
  "geriatric-rehab": "geriatric-rehabilitation",
  "ortho-sports":    "orthopaedic-sports-rehabilitation",
};

export default function ConditionPage() {
  const { slug: rawSlug } = useParams();
  const slug = SLUG_ALIAS[rawSlug] || rawSlug;
  const condition = getCondition(slug);

  // Set unique <title>, meta description, self-canonical, and breadcrumb schema per page.
  useEffect(() => {
    window.scrollTo(0, 0);
    if (!condition) return;

    const meta = SLUG_META[slug] || {};
    const canonical = `https://physiosewa.in/physiotherapy/${slug}`;

    document.title = meta.title || `${condition.title} | PhysioSewa`;

    // Meta description
    let descEl = document.querySelector('meta[name="description"]');
    if (!descEl) {
      descEl = document.createElement("meta");
      descEl.setAttribute("name", "description");
      document.head.appendChild(descEl);
    }
    const prevDesc = descEl.getAttribute("content");
    descEl.setAttribute("content", meta.desc || condition.summary);

    // Self-referencing canonical
    let canEl = document.querySelector('link[rel="canonical"]');
    const prevCanon = canEl ? canEl.getAttribute("href") : null;
    if (!canEl) {
      canEl = document.createElement("link");
      canEl.setAttribute("rel", "canonical");
      document.head.appendChild(canEl);
    }
    canEl.setAttribute("href", canonical);

    // BreadcrumbList JSON-LD
    const breadcrumb = {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: "https://physiosewa.in/" },
        { "@type": "ListItem", position: 2, name: "Physiotherapy", item: "https://physiosewa.in/#services" },
        { "@type": "ListItem", position: 3, name: condition.title, item: canonical },
      ],
    };
    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.id = "breadcrumb-jsonld";
    script.textContent = JSON.stringify(breadcrumb);
    document.getElementById("breadcrumb-jsonld")?.remove();
    document.head.appendChild(script);

    // Cleanup: restore homepage description/canonical on unmount so we never leak this page's SEO
    return () => {
      if (prevDesc) descEl.setAttribute("content", prevDesc);
      if (prevCanon) canEl.setAttribute("href", prevCanon);
      document.getElementById("breadcrumb-jsonld")?.remove();
    };
  }, [slug, condition]);

  if (!condition) {
    return (
      <div style={{ minHeight: "70vh" }} className="flex items-center justify-center px-6">
        <div className="text-center">
          <h2 className="heading-lg" style={{ fontSize: "2rem" }}>Condition not found</h2>
          <p className="mt-2" style={{ color: "var(--text-mid)" }}>The page you are looking for doesn't exist.</p>
          <Link to="/#services" className="btn-primary-pill mt-6 inline-flex">← Back to All Services</Link>
        </div>
      </div>
    );
  }

  const related = getRelated(slug, 3);

  return (
    <div className="App" data-testid="condition-page" style={{ background: "white" }}>
      {/* HERO */}
      <section
        className="relative pt-[120px] pb-16 lg:pb-24"
        style={{
          background: "linear-gradient(135deg, #F0FAF4 0%, #FFFFFF 60%)",
          borderBottom: "1px solid rgba(168,213,181,0.4)",
        }}
        data-testid="condition-hero"
      >
        <div className="max-w-5xl mx-auto px-5 lg:px-8">
          <Link
            to="/#services"
            className="inline-flex items-center gap-2 text-sm font-semibold mb-5"
            style={{ color: "var(--primary)" }}
            data-testid="condition-back-top"
          >
            <i className="fa-solid fa-arrow-left"></i> Back to All Services
          </Link>
          <div className="flex items-start gap-5 flex-wrap">
            <div
              className="flex items-center justify-center flex-shrink-0"
              style={{
                width: "76px", height: "76px",
                borderRadius: "20px",
                background: "white",
                color: "var(--primary)",
                fontSize: "1.9rem",
                boxShadow: "0 12px 30px rgba(109,191,138,0.25)",
                border: "1px solid rgba(168,213,181,0.45)",
              }}
            >
              <i className={`fa-solid ${condition.icon}`}></i>
            </div>
            <div className="flex-1 min-w-[260px]">
              <span className="section-label" data-testid="condition-label">PhysioSewa Specialty</span>
              <h1 className="heading-xl mt-1" style={{ fontSize: "clamp(2.1rem, 4.4vw, 3.2rem)" }}>
                {condition.title}
              </h1>
              <p className="mt-3 text-base md:text-lg" style={{ color: "var(--text-mid)", maxWidth: 720, lineHeight: 1.6 }}>
                {condition.summary}
              </p>
              <div className="mt-6 flex flex-wrap gap-3">
                <a
                  href={WA_BOOK}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-primary-pill"
                  data-testid="condition-book-btn"
                >
                  Book Consultation <i className="fa-solid fa-arrow-right"></i>
                </a>
                <a
                  href={WA_ENQUIRY}
                  target="_blank" rel="noopener noreferrer" className="btn-outline-pill"
                  data-testid="condition-whatsapp-btn"
                >
                  <i className="fa-brands fa-whatsapp" style={{ color: "#25D366" }}></i> WhatsApp Us
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* WHAT IS IT */}
      <section className="py-16 lg:py-20" style={{ background: "white" }} data-testid="condition-what">
        <div className="max-w-5xl mx-auto px-5 lg:px-8 grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-7">
            <span className="section-label">What Is It?</span>
            <h2 className="heading-lg" style={{ fontSize: "clamp(1.7rem, 3vw, 2.2rem)" }}>
              About {condition.title}
            </h2>
            <div className="mt-4 space-y-4" style={{ color: "var(--text-mid)", lineHeight: 1.75 }}>
              {condition.what.map((p, i) => <p key={i}>{p}</p>)}
            </div>
          </div>
          <div className="lg:col-span-5">
            <div
              className="rounded-2xl p-6"
              style={{
                background: "var(--primary-pale)",
                border: "1px solid rgba(168,213,181,0.55)",
              }}
            >
              <div className="flex items-center gap-2 text-sm font-semibold" style={{ color: "var(--primary)" }}>
                <i className="fa-solid fa-clock"></i> RECOVERY TIMELINE
              </div>
              <div className="mt-3 text-lg font-bold" style={{ color: "var(--text-dark)", lineHeight: 1.5 }}>
                {condition.recovery}
              </div>
              <div
                className="mt-5 pt-5 text-sm"
                style={{ borderTop: "1px solid rgba(109,191,138,0.35)", color: "var(--text-mid)", lineHeight: 1.65 }}
              >
                Every patient is different. Your therapist will design a plan around your exact condition,
                lifestyle and recovery goals.
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* SYMPTOMS */}
      <section className="py-16 lg:py-20" style={{ background: "var(--primary-pale)" }} data-testid="condition-symptoms">
        <div className="max-w-5xl mx-auto px-5 lg:px-8">
          <span className="section-label">Symptoms We Treat</span>
          <h2 className="heading-lg" style={{ fontSize: "clamp(1.7rem, 3vw, 2.2rem)" }}>
            Conditions and Symptoms Covered
          </h2>
          <ul className="mt-7 grid sm:grid-cols-2 gap-x-6 gap-y-3" style={{ color: "var(--text-mid)" }}>
            {condition.symptoms.map((s, i) => (
              <li key={i} className="flex items-start gap-3" data-testid={`condition-symptom-${i}`}>
                <i className="fa-solid fa-circle-check mt-1.5" style={{ color: "var(--primary)" }}></i>
                <span style={{ fontWeight: 500 }}>{s}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* TREATMENT APPROACH */}
      <section className="py-16 lg:py-20 bg-white" data-testid="condition-treatment">
        <div className="max-w-5xl mx-auto px-5 lg:px-8">
          <span className="section-label">Our Treatment Approach</span>
          <h2 className="heading-lg" style={{ fontSize: "clamp(1.7rem, 3vw, 2.2rem)" }}>
            A 3-step plan, designed for {condition.title.toLowerCase()}
          </h2>
          <div className="mt-10 grid md:grid-cols-3 gap-6">
            {condition.treatment.map((t, i) => (
              <div key={i} className="card-soft p-6" data-testid={`condition-step-${i}`}>
                <div
                  className="w-11 h-11 rounded-full flex items-center justify-center font-extrabold"
                  style={{ background: "var(--primary)", color: "white" }}
                >
                  {i + 1}
                </div>
                <h3 className="mt-4 text-lg font-bold" style={{ color: "var(--text-dark)" }}>{t.title}</h3>
                <p className="mt-1.5 text-sm" style={{ color: "var(--text-mid)", lineHeight: 1.6 }}>{t.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* RELATED */}
      <section className="py-16 lg:py-20" style={{ background: "var(--primary-pale)" }} data-testid="condition-related">
        <div className="max-w-5xl mx-auto px-5 lg:px-8">
          <span className="section-label">Related Specialties</span>
          <h2 className="heading-lg" style={{ fontSize: "clamp(1.7rem, 3vw, 2.2rem)" }}>You may also be interested in</h2>
          <div className="mt-8 grid md:grid-cols-3 gap-5">
            {related.map((r, i) => (
              <Link
                key={r.slug}
                to={`/services/${r.slug}`}
                className="card-soft p-6 block"
                data-testid={`related-${i}`}
                style={{ textDecoration: "none" }}
              >
                <div className="icon-bubble"><i className={`fa-solid ${r.icon}`}></i></div>
                <h3 className="mt-4 text-lg font-bold" style={{ color: "var(--text-dark)" }}>{r.title}</h3>
                <p className="mt-1.5 text-sm" style={{ color: "var(--text-mid)", lineHeight: 1.55 }}>{r.summary}</p>
                <div className="mt-4 text-sm font-semibold inline-flex items-center gap-1.5" style={{ color: "var(--primary)" }}>
                  Learn More <i className="fa-solid fa-arrow-right"></i>
                </div>
              </Link>
            ))}
          </div>
          <div className="mt-10 text-center">
            <Link to="/#services" className="btn-outline-pill inline-flex" data-testid="condition-back-bottom">
              <i className="fa-solid fa-arrow-left"></i> Back to All Services
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}

// re-export so tree-shake doesn't drop conditions list
export { CONDITIONS };
